import { supabase } from '../lib/supabase'

const BASE_URL = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api'

/**
 * Error thrown by the api client. Carries the HTTP status and the raw
 * backend `detail` so callers can branch on them (e.g. show a specific
 * gate for `account_status:pending`), but the `.message` is ALWAYS a
 * user-friendly PT-PT string so `alert(err.message)` never surfaces a
 * Python stack trace or `HTTP 500` to the user.
 */
export class ApiError extends Error {
  status: number
  detail: string
  constructor(status: number, detail: string, friendlyMessage: string) {
    super(friendlyMessage)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}

/**
 * Turn a backend error response into a friendly PT-PT message. The raw
 * `detail` is kept on the error for callers that need to branch (e.g.
 * `account_status:pending` triggers a specific gate, not a generic
 * alert). Anything we don't recognise falls back to a generic phrase —
 * technical strings (`KeyError`, `NoneType`, `HTTP 500`) never leak.
 */
function friendlyFromStatus(status: number, detail: string): string {
  // Account status — preserve the structured form so AccountStatusGate
  // can pick it up. Not meant for direct display.
  if (detail.startsWith('account_status:')) return detail

  switch (status) {
    case 400:
      // Validation — the backend usually provides a descriptive detail
      // (e.g. "Budget must be positive"). Trust it when present.
      return detail || 'Pedido inválido.'
    case 401:
      return 'Sessão expirou. Vamos terminar sessão para entrares de novo.'
    case 402:
      return 'Atingiste o limite do teu plano este mês. Contacta-nos se precisas de mais.'
    case 403:
      // Budget hard-stop is signalled by detail="budget:hard"
      if (detail.startsWith('budget:')) {
        return 'Atingiste o limite mensal da tua conta. Volta no próximo mês ou fala com o admin.'
      }
      return detail || 'Não tens acesso a isto.'
    case 404:
      return detail || 'Não encontrado.'
    case 409:
      return detail || 'Conflito — esta acção já foi feita ou colide com outra.'
    case 413:
      return 'Ficheiro demasiado grande. O limite é 50 MB.'
    case 422:
      return detail || 'Dados inválidos.'
    case 429:
      return 'Muitos pedidos em pouco tempo. Espera um minuto e tenta de novo.'
    case 502:
    case 503:
    case 504:
      return 'O serviço está temporariamente fora. Tenta daqui a uns segundos.'
    case 500:
      return 'Erro inesperado no servidor. Já estamos a ver.'
    default:
      return detail || `Erro ${status}.`
  }
}

/** Hook that the AuthProvider can register to react to 401s. */
let onUnauthorized: (() => void) | null = null
export function setOnUnauthorized(cb: (() => void) | null) {
  onUnauthorized = cb
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new ApiError(401, 'no_session', 'Não estás autenticado.')
  }
  return {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  }
}

async function parseErrorBody(response: Response): Promise<string> {
  try {
    const body = await response.json()
    if (typeof body.detail === 'string') return body.detail
    if (body.detail) return JSON.stringify(body.detail)
    return ''
  } catch {
    return ''
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  let headers: Record<string, string>
  try {
    headers = await getAuthHeaders()
  } catch (e) {
    if (e instanceof ApiError) throw e
    throw new ApiError(0, String(e), 'Sem ligação à internet.')
  }

  let response: Response
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...options,
      headers: { ...headers, ...options.headers },
    })
  } catch (e) {
    // TypeError from fetch = network failure, CORS, offline, etc.
    throw new ApiError(0, String(e), 'Sem ligação ao servidor. Verifica a tua internet.')
  }

  if (!response.ok) {
    const detail = await parseErrorBody(response)
    const friendly = friendlyFromStatus(response.status, detail)
    // 401 after we had a session = token rotted; tell AuthProvider.
    if (response.status === 401 && onUnauthorized) {
      try { onUnauthorized() } catch { /* best-effort */ }
    }
    throw new ApiError(response.status, detail, friendly)
  }

  if (response.status === 204) return undefined as T
  return response.json()
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  delete: (path: string) => request(path, { method: 'DELETE' }),
  postForm: async <T>(path: string, form: FormData): Promise<T> => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      throw new ApiError(401, 'no_session', 'Não estás autenticado.')
    }
    let response: Response
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}` },
        body: form,
      })
    } catch (e) {
      throw new ApiError(0, String(e), 'Sem ligação ao servidor.')
    }
    if (!response.ok) {
      const detail = await parseErrorBody(response)
      const friendly = friendlyFromStatus(response.status, detail)
      if (response.status === 401 && onUnauthorized) {
        try { onUnauthorized() } catch { /* best-effort */ }
      }
      throw new ApiError(response.status, detail, friendly)
    }
    return response.json()
  },
}

const STREAM_IDLE_TIMEOUT_MS = 60_000

/** Event shapes emitted by the chat SSE stream (services/chat.py::stream_response). */
export type StreamEvent =
  | { type: 'text_delta'; content: string }
  | { type: 'tool_use_start'; name: string; input?: unknown }
  | { type: 'tool_result'; name: string; result_preview?: string }
  | { type: 'done'; message_id?: string }
  | { type: 'error'; error: string }

/** Event shape emitted by /onboarding/regenerate (api/onboarding.py). */
export type RegenerateEvent =
  | { step: 'fetching' | 'analyzing' | 'writing' | 'done'; message?: string }
  | { step: 'error'; message: string }

export async function streamRequest<E = unknown>(
  path: string,
  body: unknown,
  onEvent: (data: E) => void,
  signal?: AbortSignal,
): Promise<void> {
  const headers = await getAuthHeaders()
  let response: Response
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal,
    })
  } catch (e) {
    throw new ApiError(0, String(e), 'Sem ligação ao servidor.')
  }

  if (!response.ok || !response.body) {
    const detail = await parseErrorBody(response)
    const friendly = friendlyFromStatus(response.status, detail)
    if (response.status === 401 && onUnauthorized) {
      try { onUnauthorized() } catch { /* best-effort */ }
    }
    throw new ApiError(response.status, detail, friendly)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  // Idle watchdog — if no bytes arrive for STREAM_IDLE_TIMEOUT_MS, abort so the
  // caller doesn't stay blocked on a hung connection.
  let idleTimer: ReturnType<typeof setTimeout> | null = null
  let aborted = false
  const resetIdle = () => {
    if (idleTimer) clearTimeout(idleTimer)
    idleTimer = setTimeout(() => {
      aborted = true
      reader.cancel('idle-timeout').catch(() => {})
    }, STREAM_IDLE_TIMEOUT_MS)
  }
  const onExternalAbort = () => { reader.cancel('aborted').catch(() => {}) }
  signal?.addEventListener('abort', onExternalAbort)
  resetIdle()

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      resetIdle()

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6)) as E
            onEvent(data)
          } catch {
            // Skip invalid JSON
          }
        }
      }
    }
    if (aborted) throw new ApiError(0, 'idle-timeout', 'Sem resposta do servidor — tenta de novo.')
  } finally {
    if (idleTimer) clearTimeout(idleTimer)
    signal?.removeEventListener('abort', onExternalAbort)
  }
}
